import { fmtTs, type TrailEvent } from '@klukka/core';
import type { CSSProperties, ReactNode } from 'react';

/** Small pieces both portals render identically. */

const STATUS_COLOR: Record<string, string> = {
  pending: 'var(--warn)',
  approved: 'var(--ok)',
  rejected: 'var(--danger)',
  submitted: 'var(--ink2)',
  draft: 'var(--ink3)',
};

export function Chip({ color, children, style }: { color: string; children: ReactNode; style?: CSSProperties }) {
  return <span className="chip" style={{ borderColor: color, color, ...style }}>{children}</span>;
}

export function StatusChip({ status, label }: { status: string; label: string }) {
  return <Chip color={STATUS_COLOR[status] ?? STATUS_COLOR.draft}>{label}</Chip>;
}

/** Two lines per event: who did what, then when. */
export function Trail({ trail }: { trail: TrailEvent[] }) {
  return (
    <div className="trail">
      {trail.map((ev, i) => (
        <div key={i} className="trail-ev">
          <span className="trail-dot" style={{ background: ev.role === 'employer' ? 'var(--ok)' : 'var(--primary)' }} />
          <span className="trail-main">
            <span><b>{ev.who}</b> — {ev.action}</span>
            <span className="trail-ts">{fmtTs(ev.ts)}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

export function StackBar({ parts, total }: { parts: Array<{ label: string; value: number; color: string }>; total: number }) {
  const safe = total || 1;
  return (
    <div>
      <div className="stack">
        {parts.map((p, i) => (
          <div key={i} style={{ width: `${(p.value / safe) * 100}%`, background: p.color }} title={p.label} />
        ))}
      </div>
      <div className="legend3" style={{ marginTop: 8 }}>
        {parts.map((p, i) => (
          <span key={i}><i style={{ background: p.color }} />{p.label} · {Math.round((p.value / safe) * 100)}%</span>
        ))}
      </div>
    </div>
  );
}

export function Spinner({ label }: { label: string }) {
  return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink3)', fontSize: 14 }}>
      {label}
    </div>
  );
}

export function ErrorBanner({ message, onRetry, retryLabel }: { message: string; onRetry?: () => void; retryLabel?: string }) {
  return (
    <div
      className="card"
      style={{ borderColor: 'var(--danger)', color: 'var(--danger)', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}
      role="alert"
    >
      <span style={{ fontSize: 13, fontWeight: 600 }}>{message}</span>
      {onRetry ? <button className="btn-sm no" onClick={onRetry}>{retryLabel ?? 'Retry'}</button> : null}
    </div>
  );
}
